import { InputAttributes, NumberFormatValues, NumericFormatProps } from "react-number-format/types/types";
import { NumberFormatBase, useNumericFormat } from "react-number-format";
import cn from "classnames";
import React, { useEffect, useState } from "react";
import styles from "./numeric-input.module.scss";
import { IValidateError } from "../../lib";
import { useSharedTranslations } from "../../lib/i18n";
import { useTranslation } from "react-i18next";

interface INumericInputProps {
    className?: string;
    value?: string | null;
    prefix?: string;
    suffix?: string;
    disabled?: boolean;
    displayType?: "text" | "input";
    validate?: boolean;
    hint?: string;
    validateFunc?: (value?: string | null) => IValidateError;
    onValidate?: (error: IValidateError) => void;
    triggerValidate?: number;
    decimalScale?: number;
    onUpdate?: (value: string | null) => void;
}

const NumericFormat = <BaseType extends any = InputAttributes>(props: NumericFormatProps<BaseType>) => {
    const numericFormatProps = useNumericFormat(props);

    return (
        <NumberFormatBase
            {...numericFormatProps}
            format={numStr =>
                (numStr === "" || numStr === "-") && props.suffix !== undefined ? `${numStr}${props.suffix}`
                    : numericFormatProps && numericFormatProps.format && numericFormatProps.format(numStr)
            }
        />
    );
};

export const NumericInput: React.FC<INumericInputProps> = ({
                                                               className,
                                                               value,
                                                               prefix,
                                                               suffix,
                                                               disabled,
                                                               displayType = "input",
                                                               hint,
                                                               decimalScale,
                                                               validate,
                                                               validateFunc,
                                                               onValidate,
                                                               triggerValidate,
                                                               onUpdate
                                                           }) => {
    const [currentValue, setCurrentValue] = useState<string>("");
    const [focused, setFocused] = useState(false);
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const { i18n } = useTranslation();
    const t = useSharedTranslations();

    useEffect(() => {
        if ((triggerValidate || !!currentValue) && validate) {
            validateValue(currentValue);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [triggerValidate, validate, validateFunc, currentValue])

    useEffect(() => {
        if (isError) {
            validateValue(currentValue);
        }
    }, [i18n.language])

    useEffect(() => {
        setCurrentValue(value ? value : "");
    }, [value]);

    const validateValue = (val?: string | null) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!validateFunc) {
            if (!val) {
                error = true;
                message = t("requiredFieldError");
            }
        } else {
            const validation = validateFunc(val);
            error = !validation.valid;
            message = validation.message;
        }
        setIsError(error);
        setValidationError(message);
        onValidate && onValidate({ valid: !error, message: message });
    }

    const onChange = (val: NumberFormatValues) => {
        setCurrentValue(val.value);
        if (validate) {
            validateValue(val.value);
        }
        onUpdate && onUpdate(val.value);
    }

    return <div
        className={cn(className, disabled ? styles.disabledInputContainer : "", displayType === "text" ? styles.displayTextContainer : "")}>
        <span
            className={cn("Textinput", "Textinput_size_m", "Textinput_view_outline",
                disabled ? "Textinput_disabled" : "",
                focused ? "Textinput_focused" : "",
                isError ? "Textinput_state_error" : "")}>
            <NumericFormat
                className={cn("Textinput-Control", disabled ? styles.numericFormatDisabled : styles.numericFormat)}
                value={currentValue}
                decimalSeparator=","
                thousandsGroupStyle="thousand"
                prefix={prefix ? `${prefix} ` : ""}
                suffix={suffix ? ` ${suffix}` : ""}
                decimalScale={decimalScale}
                thousandSeparator=" "
                disabled={disabled}
                displayType={displayType}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onValueChange={(values) => onChange(values)}
            />
            {displayType === "input" && <span className="Textinput-Box"></span>}
            {(hint || isError) && <span className="Textinput-Hint">{isError ? validationError : hint}</span>}
        </span>
    </div>;
};
